import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Recording a clip from the microphone.
 *
 * `MediaRecorder` produces a different container on nearly every browser, and the
 * one that matters is Safari on iOS: it records `audio/mp4` and refuses WebM
 * outright, while Chrome and Firefox do the opposite. So the type is probed
 * rather than declared, and the filename follows the type — a transcription
 * service demuxes by container, and `dictation.webm` holding AAC is a 400 from
 * the provider with a message about the file being corrupt.
 *
 * Everything here fails soft. A refused permission, a browser with no recorder,
 * a laptop with no microphone: all of them end with `supported` or `error` set
 * and the sheet falling back to the keyboard, which needs none of this.
 */

/** Candidates in the order we would rather have them. */
const TYPES = [
  // Opus is a quarter of the size of AAC at speech quality, and every provider
  // takes it.
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  // Safari. `audio/mp4` on iOS 17+; the bare form is what it answers to.
  'audio/mp4',
  'audio/mpeg',
] as const

const EXTENSIONS: Record<string, string> = {
  webm: 'webm',
  ogg: 'ogg',
  mp4: 'm4a',
  mpeg: 'mp3',
}

export type Recording = { blob: Blob; filename: string; seconds: number }

/** The type this browser will actually record, or null when it will not. */
export function recorderMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null

  for (const type of TYPES) {
    // `isTypeSupported` is itself missing on some older WebViews.
    if (typeof MediaRecorder.isTypeSupported !== 'function') break
    if (MediaRecorder.isTypeSupported(type)) return type
  }

  // A recorder that cannot answer the question may still work with its default,
  // and the server accepts what the browser then names in the blob's type.
  return typeof MediaRecorder.isTypeSupported === 'function' ? null : ''
}

function filenameFor(mimeType: string): string {
  const subtype = /^audio\/([a-z0-9]+)/.exec(mimeType)?.[1] ?? 'webm'
  return `dictation.${EXTENSIONS[subtype] ?? 'webm'}`
}

export function useRecorder({ maxSeconds = 120 }: { maxSeconds?: number } = {}) {
  const [recording, setRecording] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const recorder = useRef<MediaRecorder | null>(null)
  const chunks = useRef<Blob[]>([])
  const started = useRef(0)
  const ticker = useRef<ReturnType<typeof setInterval> | null>(null)
  const settle = useRef<((clip: Recording | null) => void) | null>(null)

  const supported = typeof navigator !== 'undefined' && recorderMimeType() !== null

  const cleanup = useCallback(() => {
    if (ticker.current !== null) clearInterval(ticker.current)
    ticker.current = null
    recorder.current?.stream.getTracks().forEach((track) => track.stop())
    recorder.current = null
    setRecording(false)
  }, [])

  // A component unmounted mid-recording must not leave the microphone light on.
  useEffect(() => cleanup, [cleanup])

  const stop = useCallback(() => {
    if (recorder.current?.state === 'recording') recorder.current.stop()
  }, [])

  const start = useCallback(async () => {
    setError(null)
    chunks.current = []
    setSeconds(0)

    let stream: MediaStream

    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (cause) {
      // A refusal and an absent microphone read the same to a person standing
      // there, and neither is worth a stack trace.
      setError(
        cause instanceof Error && cause.name === 'NotAllowedError'
          ? 'This browser is not allowing the microphone. Type it instead, or use the keyboard’s microphone.'
          : 'No microphone was available. Type it instead.',
      )
      return null
    }

    const mimeType = recorderMimeType()

    let media: MediaRecorder

    try {
      media = new MediaRecorder(stream, mimeType ? { mimeType } : {})
    } catch {
      stream.getTracks().forEach((track) => track.stop())
      setError('This browser cannot record audio. Type it instead.')
      return null
    }

    recorder.current = media
    started.current = Date.now()
    setRecording(true)

    media.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.current.push(event.data)
    })

    const finished = new Promise<Recording | null>((resolve) => {
      settle.current = resolve
    })

    media.addEventListener('error', () => {
      setError('The recording stopped unexpectedly. Type it instead.')
      cleanup()
      settle.current?.(null)
    })

    media.addEventListener('stop', () => {
      const elapsed = Math.max(1, Math.round((Date.now() - started.current) / 1000))
      const type = media.mimeType || mimeType || 'audio/webm'
      const blob = new Blob(chunks.current, { type })

      cleanup()
      setSeconds(elapsed)

      settle.current?.(
        blob.size > 0 ? { blob, filename: filenameFor(type), seconds: elapsed } : null,
      )
    })

    // A phone in a pocket is the reason this exists, and the server refuses a
    // long clip anyway — better to stop at the cap than to upload and be told.
    ticker.current = setInterval(() => {
      const elapsed = Math.round((Date.now() - started.current) / 1000)
      setSeconds(elapsed)
      if (elapsed >= maxSeconds) stop()
    }, 250)

    media.start()

    return finished
  }, [cleanup, maxSeconds, stop])

  return { supported, recording, seconds, error, start, stop }
}
